import { useCallback, useRef, useState } from 'react';
import type { Stroke, Version } from '../types.ts';
import { flattenToPng, strokesToPng, type Tool } from '../components/AnnotationCanvas.tsx';

/**
 * One shared empty array rather than a fresh `[]` per call.
 *
 * The result of `strokesOn` is handed to the annotation layer as a prop, and a new array every
 * time means a new prop every time — which is enough on its own to make the canvas repaint on
 * every render, however little has actually changed.
 */
const NO_STROKES: Stroke[] = [];

/** What the API expects alongside a comment or a status change. */
export interface AnnotationPayload {
  frame: number;
  strokes: unknown;
  png: string | undefined;
}

export interface AnnotationDraft {
  enabled: boolean;
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  toggle: () => void;
  strokes: Stroke[];
  /** The frame the pending drawing belongs to, or null when nothing is drawn. */
  drawFrame: number | null;
  /** The version it was drawn on, so it is saved against that clip even after switching. */
  drawVersionId: string | null;
  tool: Tool;
  setTool: (tool: Tool) => void;
  color: string;
  setColor: (color: string) => void;
  lineWidth: number;
  setLineWidth: (width: number) => void;
  /** Replace the drawing (what the canvas hands back while drawing). */
  replace: (next: Stroke[]) => void;
  undo: () => void;
  clear: () => void;
  /** The drawing, but only on the frame it was made on — elsewhere the canvas stays empty. */
  strokesOn: (frame: number) => Stroke[];
  /**
   * The frame a saved annotation belongs to: where it was drawn, or the current frame when
   * nothing is drawn (a comment still gets anchored).
   */
  frameFor: (currentFrame: number) => number;
  /**
   * Rasterised payload for the API, or undefined when nothing was drawn. Pass the frame snapshot
   * captured while the pen was down and the drawing is flattened over it (the thumbnail then
   * shows the frame); without one it falls back to strokes on transparency.
   */
  payloadFor: (
    version: Version,
    currentFrame: number,
    background?: HTMLCanvasElement | null,
  ) => AnnotationPayload | undefined;
}

/**
 * The scratch pad on top of the player: a drawing pinned to the frame it was made on, saved
 * with the next comment/rejection — or, when the reviewer moves off that frame, switches clip or
 * closes the page, saved on its own (see flushDraft in ReviewPage).
 *
 * `currentFrame` is read through a ref so the pinning survives handlers that were registered
 * once and never re-created.
 */
export function useAnnotationDraft(currentFrame: number, currentVersionId: string | null): AnnotationDraft {
  const [enabled, setEnabled] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  // The frame and clip the current scratch drawing belongs to; it is only shown there.
  const [drawFrame, setDrawFrame] = useState<number | null>(null);
  const [drawVersionId, setDrawVersionId] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#ff3b30');
  const [lineWidth, setLineWidth] = useState(4);

  const frameRef = useRef(currentFrame);
  frameRef.current = currentFrame;
  const versionRef = useRef(currentVersionId);
  versionRef.current = currentVersionId;

  const clear = useCallback(() => {
    setStrokes([]);
    setDrawFrame(null);
    setDrawVersionId(null);
  }, []);

  /*
   * A drawing belongs to the frame it was made on. It used to keep the *first* such frame for
   * the lifetime of the draft, which meant that after drawing once, every later stroke — on any
   * other frame — was silently filed under that first frame and hidden everywhere else: the
   * "you can only draw on the first frame" bug. Now an empty pad re-pins to wherever the pen
   * touches down, and leaving a frame with a drawing on it saves that drawing (ReviewPage).
   */
  const replace = useCallback((next: Stroke[]) => {
    setStrokes(next);
    if (next.length === 0) {
      setDrawFrame(null);
      setDrawVersionId(null);
      return;
    }
    setDrawFrame((prev) => prev ?? frameRef.current);
    setDrawVersionId((prev) => prev ?? versionRef.current);
  }, []);

  const undo = useCallback(() => {
    replace(strokes.slice(0, -1));
  }, [replace, strokes]);

  const toggle = useCallback(() => setEnabled((v) => !v), []);

  const strokesOn = useCallback(
    (frame: number) => (drawFrame === null || frame === drawFrame ? strokes : NO_STROKES),
    [drawFrame, strokes],
  );

  const frameFor = useCallback((frame: number) => drawFrame ?? frame, [drawFrame]);

  const payloadFor = useCallback(
    (version: Version, frame: number, background?: HTMLCanvasElement | null) =>
      strokes.length > 0
        ? {
            frame: drawFrame ?? frame,
            strokes: strokes as unknown,
            png: background
              ? flattenToPng(background, strokes)
              : strokesToPng(strokes, version.width ?? 1920, version.height ?? 1080),
          }
        : undefined,
    [strokes, drawFrame],
  );

  return {
    enabled,
    setEnabled,
    toggle,
    strokes,
    drawFrame,
    drawVersionId,
    tool,
    setTool,
    color,
    setColor,
    lineWidth,
    setLineWidth,
    replace,
    undo,
    clear,
    strokesOn,
    frameFor,
    payloadFor,
  };
}
